import { get } from "lodash";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { useFormAPI, useFormStore } from "./index";
import type { FormValues } from "./types";

export const useYearRangeToggle = (fieldPath: string) => {
    const { getFieldValue, setFieldValue } = useFormAPI();

    const yearPath = `${fieldPath}.date.year` as PrefixObjectAccessor<FormValues, []>;
    const endYearPath = `${fieldPath}.date.endYear` as PrefixObjectAccessor<FormValues, []>;
    const proximityPath = `${fieldPath}.dateProximity.year` as PrefixObjectAccessor<FormValues, []>;
    const rangeModePath = `${fieldPath}.isRangeMode` as PrefixObjectAccessor<FormValues, []>;

    const isRangeMode = useFormStore(({ values }) => Boolean(get(values, rangeModePath)));

    const toggle = () => {
        const nextMode = !isRangeMode;

        if (nextMode) {
            // Single → Range: date.year becomes start year, compute end year
            const currentYear = getFieldValue(yearPath) as number | null;
            const endYear = getFieldValue(endYearPath) as number | null;
            const proximity = getFieldValue(proximityPath) as number | null;
            const shouldOverrideEndYear = !endYear;

            if (shouldOverrideEndYear && proximity && currentYear) {
                // If proximity was set, start = year - proximity, end = year + proximity
                setFieldValue(yearPath, currentYear - proximity);
                setFieldValue(endYearPath, currentYear + proximity);
            }
            if (shouldOverrideEndYear && proximity === 0) {
                // If exact year (proximity explicitly 0), keep year as start, copy to end
                setFieldValue(endYearPath, currentYear);
            }
        } else {
            // Range → Single: keep date.year (start) as the single year, clear endYear
            const startYear = getFieldValue(yearPath) as number | null;
            const endYear = getFieldValue(endYearPath) as number | null;

            if (!startYear && endYear) {
                // If only end year is filled, move it to date.year
                setFieldValue(yearPath, endYear);
            }
            // Clear end year and stale proximity data from range mode
            setFieldValue(endYearPath, null);
            setFieldValue(proximityPath, null);
        }

        setFieldValue(rangeModePath, nextMode);
    };

    return { isRangeMode, toggle };
};
